import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNotices } from '../context/NoticeContext';
import NoticeDetail from './NoticeDetail';
import NoticeForm from './NoticeForm';

interface NoticeListProps {
  onBack: () => void;
}

const NoticeList: React.FC<NoticeListProps> = ({ onBack }) => {
  const { notices, loading, error, pagination, fetchNotices } = useNotices();
  const [selectedNoticeId, setSelectedNoticeId] = useState<number | null>(null);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<boolean>(false);

  useEffect(() => {
    fetchNotices();
  }, [fetchNotices]);

  const handlePageChange = (page: number) => {
    fetchNotices(page);
  };

  const handleNoticeClick = (id: number) => {
    setSelectedNoticeId(id);
  };

  const handleBackToList = () => {
    setSelectedNoticeId(null);
    setShowForm(false);
    setEditMode(false);
    fetchNotices(pagination.page);
  };

  const handleNewNotice = () => {
    setShowForm(true);
    setEditMode(false);
    setSelectedNoticeId(null);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
      date.getDate()
    ).padStart(2, '0')}`;
  };

  if (selectedNoticeId) {
    return <NoticeDetail id={selectedNoticeId} onBack={handleBackToList} />;
  }

  if (showForm) {
    const editId = editMode && selectedNoticeId ? selectedNoticeId : undefined;

    return <NoticeForm onBack={handleBackToList} editId={editId} />;
  }

  return (
    <Container>
      <Header>
        <BackButton onClick={onBack}>
          <BackIcon>&larr;</BackIcon>
          <span>뒤로</span>
        </BackButton>
        <Title>공지사항</Title>
        <NewButton onClick={handleNewNotice}>글쓰기</NewButton>
      </Header>

      {loading ? (
        <LoadingMessage>로딩 중...</LoadingMessage>
      ) : error ? (
        <ErrorMessage>{error}</ErrorMessage>
      ) : (
        <>
          <NoticeTable>
            <thead>
              <tr>
                <TableHeader width="10%">번호</TableHeader>
                <TableHeader width="60%">제목</TableHeader>
                <TableHeader width="15%">등록일</TableHeader>
                <TableHeader width="15%">조회수</TableHeader>
              </tr>
            </thead>
            <tbody>
              {notices.length === 0 ? (
                <tr>
                  <TableCell colSpan={4}>등록된 공지사항이 없습니다.</TableCell>
                </tr>
              ) : (
                notices.map((notice) => (
                  <TableRow key={notice.id} onClick={() => handleNoticeClick(notice.id!)}>
                    <TableCell>{notice.id}</TableCell>
                    <TableCell align="left">{notice.title}</TableCell>
                    <TableCell>{notice.created_at && formatDate(notice.created_at)}</TableCell>
                    <TableCell text-align="left">{notice.views}</TableCell>
                  </TableRow>
                ))
              )}
            </tbody>
          </NoticeTable>

          <Pagination>
            {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((page) => (
              <PageButton
                key={page}
                active={page === pagination.page}
                onClick={() => handlePageChange(page)}
              >
                {page}
              </PageButton>
            ))}
          </Pagination>
        </>
      )}
    </Container>
  );
};

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  position: relative;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 5px;
  position: absolute;
  left: 0;
`;

const BackIcon = styled.span`
  font-size: 20px;
  margin-right: 5px;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 600;
  flex: 1;
  text-align: center;
`;

const NewButton = styled.button`
  padding: 8px 16px;
  background-color: #4a90e2;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  position: absolute;
  right: 0;

  &:hover {
    background-color: #357ab7;
  }
`;

const NoticeTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;
`;

const TableHeader = styled.th<{ width: string }>`
  padding: 12px;
  background-color: #f5f5f5;
  border-top: 2px solid #ddd;
  border-bottom: 1px solid #ddd;
  text-align: center;
  font-weight: 600;
  width: ${(props) => props.width};
`;

const TableRow = styled.tr`
  cursor: pointer;

  &:hover {
    background-color: #f9f9f9;
  }
`;

const TableCell = styled.td<{ align?: string }>`
  padding: 12px;
  border-bottom: 1px solid #ddd;
  text-align: ${(props) => props.align || 'center'};
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
`;

const PageButton = styled.button<{ active: boolean }>`
  padding: 8px 12px;
  margin: 0 5px;
  border: 1px solid ${(props) => (props.active ? '#4a90e2' : '#ddd')};
  background-color: ${(props) => (props.active ? '#4a90e2' : 'white')};
  color: ${(props) => (props.active ? 'white' : '#333')};
  cursor: pointer;
  border-radius: 4px;

  &:hover {
    background-color: ${(props) => (props.active ? '#357ab7' : '#f5f5f5')};
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 20px;
  font-size: 1.1rem;
  color: #666;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 20px;
  font-size: 1.1rem;
  color: #e74c3c;
`;

export default NoticeList;
